'use client';
import { useI18n } from '@masar/i18n/react';
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { IconButton } from './Button';
import { cx } from './cx';
import { useFocusTrap } from './focus-trap';
import { Icon } from './icons/Icon';
import s from './overlay.module.css';

export { useFocusTrap };

const noSubscribe = () => () => {};

/**
 * Renders into document.body in the same commit as the overlay opens, so the
 * focus trap's effect finds the dialog node and moves focus into it. The
 * server (and hydration) snapshot renders nothing.
 */
function Portal({ children }: { children: ReactNode }) {
  const onClient = useSyncExternalStore(
    noSubscribe,
    () => true,
    () => false,
  );
  return onClient ? createPortal(children, document.body) : null;
}

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  wide?: boolean;
  /** Sticky footer (actions). */
  footer?: ReactNode;
  children: ReactNode;
  /** Accessible name when the title is not plain text. */
  label?: string;
}

/** Side drawer from the inline end; a bottom sheet on phones. */
export function Drawer({ open, onClose, title, subtitle, wide, footer, children, label }: DrawerProps) {
  const { t } = useI18n();
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useFocusTrap(ref, open, onClose);
  if (!open) return null;
  return (
    <Portal>
      <div
        className={s.scrim}
        role="presentation"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div
          ref={ref}
          className={cx(s.drawer, wide && s.wide)}
          role="dialog"
          aria-modal="true"
          aria-labelledby={label ? undefined : titleId}
          aria-label={label}
          tabIndex={-1}
        >
          <div className={s.head}>
            <div>
              <h2 id={titleId}>{title}</h2>
              {subtitle ? <p>{subtitle}</p> : null}
            </div>
            <IconButton icon="x" label={t('إغلاق')} onClick={onClose} />
          </div>
          {children}
          {footer ? <div className={s.foot}>{footer}</div> : null}
        </div>
      </div>
    </Portal>
  );
}

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  wide?: boolean;
  /** role="alertdialog" for confirmations. */
  alert?: boolean;
}

/** Centered modal dialog (confirmations, invoice preview). */
export function Dialog({ open, onClose, title, children, actions, wide, alert }: DialogProps) {
  const { t } = useI18n();
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useFocusTrap(ref, open, onClose);
  if (!open) return null;
  return (
    <Portal>
      <div
        className={cx(s.scrim, s.center)}
        role="presentation"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div
          ref={ref}
          className={cx(s.dialog, wide && s.dialogWide)}
          role={alert ? 'alertdialog' : 'dialog'}
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
        >
          <div className={s.head}>
            <h2 id={titleId}>{title}</h2>
            <IconButton icon="x" label={t('إغلاق')} onClick={onClose} />
          </div>
          {children}
          {actions ? <div className={s.actions}>{actions}</div> : null}
        </div>
      </div>
    </Portal>
  );
}

/* ---------------- Toasts ---------------- */

interface ToastItem {
  id: number;
  text: ReactNode;
  tone: 'ok' | 'crit';
}
interface ToastApi {
  show: (text: ReactNode, opts?: { tone?: 'ok' | 'crit'; ms?: number }) => void;
}
const ToastCtx = createContext<ToastApi | null>(null);

/** One toast at a time, bottom center, announced politely (prototype .toast). */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [item, setItem] = useState<ToastItem | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const show = useCallback<ToastApi['show']>((text, opts) => {
    clearTimeout(timer.current);
    setItem({ id: Date.now(), text, tone: opts?.tone ?? 'ok' });
    timer.current = setTimeout(() => setItem(null), opts?.ms ?? 2800);
  }, []);
  useEffect(() => () => clearTimeout(timer.current), []);
  const api = useMemo(() => ({ show }), [show]);
  return (
    <ToastCtx value={api}>
      {children}
      <div aria-live="polite" role="status">
        {item ? (
          <div key={item.id} className={cx(s.toast, item.tone === 'crit' && s.toastCrit)}>
            <Icon name={item.tone === 'crit' ? 'alert' : 'check'} size="sm" />
            {item.text}
          </div>
        ) : null}
      </div>
    </ToastCtx>
  );
}

export function useToast(): ToastApi {
  const v = use(ToastCtx);
  if (!v) throw new Error('useToast outside <ToastProvider>');
  return v;
}

/* ---------------- Save bar ---------------- */

/**
 * Sticky bar that appears when a draft has unsaved changes (pricing, settings,
 * content). Save waits for the server; Discard throws the draft away.
 */
export function SaveBar(props: {
  message: ReactNode;
  onSave: () => void;
  onDiscard: () => void;
  saveLabel: string;
  discardLabel: string;
  busy?: boolean;
}) {
  return (
    <div
      className={s.savebar}
      role="region"
      aria-label={typeof props.message === 'string' ? props.message : undefined}
    >
      <span>
        <Icon name="edit" size="sm" />
        {props.message}
      </span>
      <div className={s.row}>
        <button type="button" className={s.saveGhost} onClick={props.onDiscard} disabled={props.busy}>
          {props.discardLabel}
        </button>
        <button
          type="button"
          className={s.saveBtn}
          onClick={props.onSave}
          disabled={props.busy}
          aria-busy={props.busy || undefined}
        >
          {props.saveLabel}
        </button>
      </div>
    </div>
  );
}
